import { DataManager } from './utils/data-manager.js';
import { FeatureFlags } from './utils/feature-flags.js';
import { ThemeController } from './utils/theme-controller.js';
import { InsightsTracker } from './utils/insights-tracker.js';

/**
 * Core Dashboard Module
 * Handles basic initialization, utilities, and core functionality
 */
export class DashboardCore {
  constructor() {
    this.data = new DataManager();
    this.analytics = new InsightsTracker(this.data);
    this.featureFlags = new FeatureFlags(this.data);
    this.modules = {};
    this.lastCompactCalendarDate = null;

    // Debug and feature flags
    this.debug = {
      headerToggle: false,
    };

    // State management
    this.stimmingEnabled = false;
    this.floatingNotesEnabled = true;
    this.celebrationsEnabled = true;
    this.floatingNotesInterval = null;
    this.themePills = [];
    this.lastAuthUserId = null;
    this.hasShownSignedOutMessage = false;

    // Layout state
    this.layoutState = {
      tier: 'spacious',
      width: window.innerWidth,
    };

    // Leave schedule constants
    this.leaveDayOrder = ['sun', 'mon', 'tue', 'wed', 'thu', 'fri', 'sat'];
    this.leaveDayMeta = {
      sun: { label: 'Sunday', short: 'Sun' },
      mon: { label: 'Monday', short: 'Mon' },
      tue: { label: 'Tuesday', short: 'Tue' },
      wed: { label: 'Wednesday', short: 'Wed' },
      thu: { label: 'Thursday', short: 'Thu' },
      fri: { label: 'Friday', short: 'Fri' },
      sat: { label: 'Saturday', short: 'Sat' },
    };

    // Countdown system IDs
    this.countdownIds = {
      work: 'system-work',
      sleep: 'system-sleep',
      appointment: 'system-next-appointment',
    };

    // Default configurations
    this.leaveScheduleDefaults = {
      entries: [],
      configured: false,
    };

    this.sleepSettingsDefaults = {
      bedtime: '22:30',
      windDownMinutes: 60,
      configured: false,
    };

    // Initialize countdown defaults
    this.countdownDefaults = this.buildCountdownDefaults();

    // Initialize state
    this.bootstrapDefaultData();
    this.initializeCountdownState();
  }

  bootstrapDefaultData() {
    const defaults = {
      ...this.leaveScheduleDefaults,
      ...this.sleepSettingsDefaults,
      ...this.countdownDefaults,
    };

    if (typeof this.data.ensureDefaults === 'function') {
      Object.keys(defaults).forEach(key => {
        if (this.data.get(key) == null) {
          this.data.set(key, defaults[key]);
        }
      });
    }
  }

  buildCountdownDefaults() {
    const now = new Date();
    const today = now.getDay();
    const daysUntilNextWork = (1 - today + 7) % 7 || 7; // Days until next Monday

    return {
      work: {
        id: this.countdownIds.work,
        label: 'Work',
        category: 'work',
        target: new Date(now.getTime() + daysUntilNextWork * 24 * 60 * 60 * 1000),
        configured: false,
      },
      sleep: {
        id: this.countdownIds.sleep,
        label: 'Bedtime',
        category: 'sleep',
        target: new Date(now.getFullYear(), now.getMonth(), now.getDate(), 22, 30),
        configured: false,
      },
      appointment: {
        id: this.countdownIds.appointment,
        label: 'Next appointment',
        category: 'appointment',
        target: null,
        configured: false,
      },
    };
  }

  initializeCountdownState() {
    this.countdownRefreshInterval = setInterval(() => {
      this.refreshCountdownStack();
    }, 60000); // Update every minute

    // Initial refresh
    this.refreshCountdownStack();
  }

  refreshCountdownStack() {
    // This will be implemented by the schedule module
    if (typeof this.refreshCountdownStackImpl === 'function') {
      this.refreshCountdownStackImpl();
    }
  }

  // Utility methods
  track(eventName, payload = {}) {
    if (this.analytics?.track) {
      this.analytics.track(eventName, payload);
    }
  }

  createLeaveEntry(label = '', leaveTime = '08:30', buffer = 15, days = []) {
    return {
      id: this.generateLeaveEntryId(),
      label,
      leaveTime,
      buffer,
      days: days.length > 0 ? days : ['mon', 'tue', 'wed', 'thu', 'fri'],
      enabled: true,
    };
  }

  generateLeaveEntryId() {
    if (typeof crypto !== 'undefined' && crypto.randomUUID) {
      return crypto.randomUUID();
    }
    return `leave-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
  }

  escapeHtml(value) {
    if (value == null) return '';
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  cloneCountdownArray(list) {
    if (!Array.isArray(list)) {
      return [];
    }
    return list.map((item) => this.cloneCountdown(item));
  }

  cloneCountdown(countdown) {
    if (typeof structuredClone === 'function') {
      try {
        return structuredClone(countdown);
      } catch (error) {
        // Fallback to JSON clone below
      }
    }

    try {
      return JSON.parse(JSON.stringify(countdown));
    } catch (error) {
      console.warn('[DashboardCore] Failed to clone countdown:', error);
      return {};
    }
  }

  // Countdown normalization methods
  normalizeCountdownCollection(list) {
    if (!Array.isArray(list)) {
      return [];
    }

    return list.map(countdown => this.normalizeCountdown(countdown)).filter(Boolean);
  }

  normalizeCountdown(rawCountdown) {
    if (!rawCountdown || typeof rawCountdown !== 'object') {
      return null;
    }

    const countdown = { ...rawCountdown };

    // Normalize target date
    if (countdown.target) {
      if (typeof countdown.target === 'string') {
        countdown.target = new Date(countdown.target);
      } else if (countdown.target instanceof Date) {
        // Keep as Date object
      } else if (typeof countdown.target === 'object' && countdown.target.seconds) {
        // Firestore timestamp
        countdown.target = new Date(countdown.target.seconds * 1000);
      }
    }

    // Normalize category
    if (!countdown.category) {
      countdown.category = this.inferCountdownCategory(countdown.id);
    }

    // Ensure required fields
    if (!countdown.id) {
      countdown.id = this.generateLeaveEntryId();
    }

    return countdown;
  }

  convertLegacyCustomCountdowns(list) {
    if (!Array.isArray(list)) {
      return [];
    }

    return list.map(countdown => {
      if (typeof countdown === 'string') {
        return {
          id: this.generateLeaveEntryId(),
          label: countdown,
          category: 'custom',
          target: null,
          configured: false,
        };
      }
      return countdown;
    });
  }

  inferCountdownCategory(id) {
    if (!id || typeof id !== 'string') {
      return 'custom';
    }

    if (id.includes('work') || id.includes('job')) {
      return 'work';
    }
    if (id.includes('sleep') || id.includes('bed')) {
      return 'sleep';
    }
    if (id.includes('appointment') || id.includes('meeting')) {
      return 'appointment';
    }
    if (id.includes('deadline') || id.includes('due')) {
      return 'deadline';
    }
    if (id.includes('event') || id.includes('party')) {
      return 'event';
    }

    return 'custom';
  }

  getCountdownById(id) {
    const countdowns = this.data.get('countdowns', []);
    return countdowns.find(c => c.id === id) || null;
  }

  // Data persistence methods
  persistCountdowns(nextCountdowns) {
    this.data.set('countdowns', nextCountdowns);
  }

  updateCountdown(id, updater) {
    const countdowns = this.data.get('countdowns', []);
    const index = countdowns.findIndex(c => c.id === id);

    if (index >= 0) {
      countdowns[index] = updater(countdowns[index]);
      this.persistCountdowns(countdowns);
      return countdowns[index];
    }

    return null;
  }

  addCountdown(countdown) {
    const countdowns = this.data.get('countdowns', []);
    countdowns.push(countdown);
    this.persistCountdowns(countdowns);
    return countdown;
  }

  removeCountdown(id) {
    const countdowns = this.data.get('countdowns', []);
    const filtered = countdowns.filter(c => c.id !== id);
    this.persistCountdowns(filtered);
    return filtered;
  }
}
